/**
 * VoiceQuickAction — FAB globale per creare un Promemoria/Appuntamento via voce.
 *
 * Flow:
 *  1) Click FAB → modale con pulsante Rec
 *  2) Registra audio (MediaRecorder) → stop → POST /api/voice/quick-action
 *  3) Backend: Whisper → Claude → crea reminder/appointment
 *  4) Mostra esito (toast + summary) + link "Apri agenda"
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Mic, MicOff, Loader2, X, Sparkles, CheckCircle2, Calendar, Clock, User } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { fmtDateTimeIT } from "@/lib/format";

// ── FAB position: draggable con persistenza (localStorage) ──────────────
const FAB_SIZE = 56; // 14 * 4 (w-14)
const FAB_MARGIN = 12;
const FAB_POS_KEY = "voice-fab-pos-v1";
const DRAG_THRESHOLD_PX = 5;

function loadStoredPos() {
  try {
    const raw = localStorage.getItem(FAB_POS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (typeof p.x === "number" && typeof p.y === "number") return p;
  } catch {}
  return null;
}

function clampToViewport(x, y) {
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  return {
    x: Math.max(FAB_MARGIN, Math.min(vw - FAB_SIZE - FAB_MARGIN, x)),
    y: Math.max(FAB_MARGIN, Math.min(vh - FAB_SIZE - FAB_MARGIN, y)),
  };
}

function defaultPos(isMobileApp) {
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  const bottomOffset = isMobileApp ? 96 : 24; // sopra la bottom-nav su mobile
  return {
    x: vw - FAB_SIZE - (isMobileApp ? 20 : 24),
    y: vh - FAB_SIZE - bottomOffset,
  };
}

function pickMime() {
  if (typeof MediaRecorder === "undefined") return null;
  for (const c of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/mpeg"]) {
    if (MediaRecorder.isTypeSupported(c)) return c;
  }
  return "";
}

export default function VoiceQuickAction() {
  const location = useLocation();
  // Nascondi il FAB sulle pagine chat del Comandante per non sovrapporsi al
  // pulsante "Invia" del chat.
  const isComandantePage = /\/comandante|\/m\/comandante/.test(location.pathname);
  const [open, setOpen] = useState(false);
  const [supported, setSupported] = useState(true);
  const [recording, setRecording] = useState(false);
  const [secs, setSecs] = useState(0);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const mrRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setSupported(false);
    }
    return () => stopAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopAll = () => {
    try { mrRef.current?.stop(); } catch {}
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    streamRef.current = null;
  };

  const reset = () => {
    setRecording(false);
    setBusy(false);
    setStage("");
    setSecs(0);
    setResult(null);
    setError("");
    chunksRef.current = [];
  };

  const closeDialog = () => {
    stopAll();
    reset();
    setOpen(false);
  };

  const startRecording = async () => {
    reset();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      mrRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        setRecording(false);
      };
      mr.start(250);
      setRecording(true);
      setSecs(0);
      timerRef.current = setInterval(() => setSecs((s) => s + 1), 1000);
    } catch (e) {
      setError("Impossibile accedere al microfono. Verifica i permessi del browser.");
      setSupported(false);
    }
  };

  const stopAndSubmit = async () => {
    if (!mrRef.current || mrRef.current.state === "inactive") return;
    setBusy(true);
    setStage("transcribe");
    // Stop recorder + wait
    await new Promise((resolve) => {
      const prev = mrRef.current.onstop;
      mrRef.current.onstop = (...args) => {
        try { prev?.(...args); } catch {}
        resolve();
      };
      try { mrRef.current.stop(); } catch { resolve(); }
    });

    if (chunksRef.current.length === 0) {
      setError("Audio non registrato. Riprova.");
      setBusy(false);
      return;
    }

    const mime = mrRef.current.mimeType || "audio/webm";
    const ext = mime.includes("mp4") ? "mp4" : mime.includes("mpeg") ? "mp3" : "webm";
    const blob = new Blob(chunksRef.current, { type: mime });
    const fd = new FormData();
    fd.append("audio", blob, `voice-quick.${ext}`);

    try {
      setStage("analyze");
      const r = await api.post("/voice/quick-action", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      setResult(r.data);
      const isAppt = r.data?.kind === "appointment";
      toast.success(`${isAppt ? "Appuntamento" : "Promemoria"} creato`, {
        description: r.data?.created?.title || "",
      });
    } catch (e) {
      const msg = formatApiError(e?.response?.data?.detail) || "Errore elaborazione vocale";
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
      setStage("");
    }
  };

  // === Render FAB always visible (draggable con posizione memorizzata) ===
  const isMobileApp = typeof window !== "undefined" && window.location.pathname.startsWith("/m/");

  // Nasconde completamente il FAB sulle pagine chat del Comandante:
  // evita conflitto con il pulsante "Invia" del chat in basso a destra.
  if (isComandantePage) return null;

  return (
    <>
      <DraggableFab
        isMobileApp={isMobileApp}
        onClick={() => setOpen(true)}
      />

      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeDialog}>
          <div onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-sm shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
            data-testid="voice-quick-dialog">
            {/* Header */}
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-gradient-to-br from-[#0A192F] to-[#172A45] text-white rounded-t-sm">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#B7955B]" />
                <div>
                  <h2 className="font-serif text-lg leading-tight">Comando vocale</h2>
                  <div className="text-[10px] uppercase tracking-wider text-[#B7955B]">Promemoria & Appuntamenti AI</div>
                </div>
              </div>
              <button onClick={closeDialog} className="text-slate-300 hover:text-white" data-testid="voice-quick-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5">
              {!supported && (
                <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-sm p-3">
                  Il microfono non è disponibile su questo browser/dispositivo o non hai dato i permessi.
                </div>
              )}

              {supported && !result && (
                <>
                  <div className="text-xs text-slate-600 mb-4 leading-relaxed">
                    Premi <strong>Registra</strong> e parla naturalmente. Esempi:
                    <ul className="mt-2 ml-3 list-disc text-slate-500 space-y-0.5">
                      <li>"Chiamare Mario Rossi domani alle 15 per esito proposta"</li>
                      <li>"Appuntamento Luca Bianchi venerdì alle 10 in ufficio"</li>
                      <li>"Promemoria controllare pratica Russo lunedì mattina"</li>
                    </ul>
                  </div>

                  <div className="flex flex-col items-center justify-center py-6 gap-3">
                    {!recording && !busy && (
                      <button
                        onClick={startRecording}
                        data-testid="voice-quick-start"
                        className="w-20 h-20 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center shadow-lg transition-transform hover:scale-105"
                      >
                        <Mic className="w-8 h-8" />
                      </button>
                    )}
                    {recording && (
                      <button
                        onClick={stopAndSubmit}
                        data-testid="voice-quick-stop"
                        className="w-20 h-20 rounded-full bg-rose-600 hover:bg-rose-700 text-white flex items-center justify-center shadow-lg animate-pulse"
                      >
                        <MicOff className="w-8 h-8" />
                      </button>
                    )}
                    {busy && (
                      <div className="flex items-center gap-2 text-slate-700">
                        <Loader2 className="w-5 h-5 animate-spin text-[#B7955B]" />
                        <span className="text-sm">
                          {stage === "transcribe" ? "Trascrizione..." : stage === "analyze" ? "Analisi AI..." : "Elaborazione..."}
                        </span>
                      </div>
                    )}

                    <div className="text-sm font-mono text-slate-700">
                      {recording ? <span className="text-rose-600">● {String(Math.floor(secs / 60)).padStart(2, "0")}:{String(secs % 60).padStart(2, "0")}</span>
                        : !busy ? <span className="text-slate-400">Premi per iniziare</span> : <span >—</span>}
                    </div>

                    {recording && (
                      <div className="text-[10px] text-slate-500 italic">Click di nuovo per fermare e inviare</div>
                    )}
                  </div>
                </>
              )}

              {error && (
                <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-sm p-3 mt-3">
                  {error}
                </div>
              )}

              {result && (
                <div className="space-y-4" data-testid="voice-quick-result">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-sm flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium text-emerald-900">
                        {result.kind === "appointment" ? "Appuntamento creato" : "Promemoria creato"}
                      </div>
                      <div className="text-xs text-emerald-700">
                        {result.clientMatched ? "Cliente associato" : result.clientNameMention ? `Cliente "${result.clientNameMention}" non trovato — promemoria libero` : "Promemoria libero (nessun cliente nominato)"}
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 space-y-2">
                    <div className="flex items-start gap-2 text-sm">
                      <span className="text-slate-500 text-[10px] uppercase tracking-wider w-16 shrink-0 mt-0.5">Titolo</span>
                      <span className="text-slate-900 font-medium flex-1">{result.created?.title}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-3.5 h-3.5 text-slate-400" />
                      <span className="text-slate-500 text-[10px] uppercase tracking-wider w-12 shrink-0">Quando</span>
                      <span className="text-slate-900 tabular-nums">{fmtDateTimeIT(result.created?.when)}</span>
                    </div>
                    {result.created?.notes && (
                      <div className="flex items-start gap-2 text-sm pt-1 border-t border-slate-200">
                        <span className="text-slate-500 text-[10px] uppercase tracking-wider w-16 shrink-0 mt-0.5">Note</span>
                        <span className="text-slate-700 text-xs flex-1">{result.created.notes}</span>
                      </div>
                    )}
                  </div>

                  <div className="bg-slate-50 border border-slate-100 rounded-sm p-2.5 text-xs text-slate-600 italic">
                    <span className="text-[9px] uppercase tracking-wider text-slate-400 not-italic mr-1">Hai detto:</span>
                    "{result.transcript}"
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={reset}
                      data-testid="voice-quick-new"
                      className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50 text-slate-700 flex items-center justify-center gap-1.5"
                    >
                      <Mic className="w-3.5 h-3.5" /> Nuova
                    </button>
                    <Link
                      to="/agenda"
                      onClick={closeDialog}
                      data-testid="voice-quick-open-agenda"
                      className="flex-1 px-3 py-2 text-sm bg-[#0A192F] hover:bg-[#172A45] text-white rounded-sm flex items-center justify-center gap-1.5"
                    >
                      <Clock className="w-3.5 h-3.5" /> Vedi in agenda
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}


/**
 * DraggableFab — bottone FAB microfono draggable
 * - Pointer events (mouse + touch unificati)
 * - Distingue click da drag (soglia 5px)
 * - Posizione persistita in localStorage
 * - Ri-clampata al resize del viewport
 * - Feedback visivo durante drag (cursor grabbing + scala)
 */
function DraggableFab({ isMobileApp, onClick }) {
  const btnRef = useRef(null);
  const [pos, setPos] = useState(() => {
    const stored = loadStoredPos();
    if (stored) return clampToViewport(stored.x, stored.y);
    return defaultPos(isMobileApp);
  });
  const [dragging, setDragging] = useState(false);
  const dragStateRef = useRef({
    active: false,
    startX: 0, startY: 0,
    origX: 0, origY: 0,
    moved: false,
  });

  // Riclampa al resize per non uscire dallo schermo
  useEffect(() => {
    const onResize = () => setPos((p) => clampToViewport(p.x, p.y));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handlePointerDown = useCallback((e) => {
    // Solo tasto sinistro / touch primario
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    const el = btnRef.current;
    if (!el) return;
    // Cattura pointer per continuare a ricevere eventi anche fuori dal bottone
    try { el.setPointerCapture(e.pointerId); } catch {}
    dragStateRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      origX: pos.x,
      origY: pos.y,
      moved: false,
      pointerId: e.pointerId,
    };
  }, [pos.x, pos.y]);

  const handlePointerMove = useCallback((e) => {
    const s = dragStateRef.current;
    if (!s.active) return;
    const dx = e.clientX - s.startX;
    const dy = e.clientY - s.startY;
    if (!s.moved && Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) {
      s.moved = true;
      setDragging(true);
    }
    if (s.moved) {
      const next = clampToViewport(s.origX + dx, s.origY + dy);
      setPos(next);
    }
  }, []);

  const handlePointerUp = useCallback((e) => {
    const s = dragStateRef.current;
    if (!s.active) return;
    s.active = false;
    try { btnRef.current?.releasePointerCapture(e.pointerId); } catch {}
    if (s.moved) {
      // Salva pos + reset dragging (con delay per evitare click residuo)
      setDragging(false);
      try { localStorage.setItem(FAB_POS_KEY, JSON.stringify(pos)); } catch {}
    } else {
      // Click puro → apri dialog
      setDragging(false);
      onClick?.();
    }
  }, [pos, onClick]);

  // Snap ai lati quando doppio-tap (per riportarlo velocemente in un angolo)
  const handleDoubleClick = useCallback(() => {
    const def = defaultPos(isMobileApp);
    setPos(def);
    try { localStorage.setItem(FAB_POS_KEY, JSON.stringify(def)); } catch {}
  }, [isMobileApp]);

  return (
    <button
      ref={btnRef}
      data-testid="voice-quick-fab"
      title="Crea promemoria via voce · Trascina per spostare · Doppio-click per resettare"
      aria-label="Registrazione vocale rapida"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onDoubleClick={handleDoubleClick}
      onContextMenu={(e) => e.preventDefault()}
      className={`fixed z-40 w-14 h-14 rounded-full text-[#B7955B] shadow-xl flex items-center justify-center select-none ${dragging ? "scale-110" : "hover:scale-105 active:scale-95"}`}
      style={{
        left: pos.x,
        top: pos.y,
        touchAction: "none",
        cursor: dragging ? "grabbing" : "grab",
        background: dragging
          ? "linear-gradient(135deg, #172A45 0%, #0A192F 100%)"
          : "#0A192F",
        boxShadow: dragging
          ? "0 20px 40px -8px rgba(10,25,47,0.55), 0 0 0 4px rgba(183,149,91,0.28)"
          : "0 8px 24px -6px rgba(10,25,47,0.4)",
        transition: dragging
          ? "background 180ms ease, box-shadow 180ms ease, transform 180ms ease"
          : "background 180ms ease, box-shadow 180ms ease, transform 180ms ease, left 200ms cubic-bezier(0.16,1,0.3,1), top 200ms cubic-bezier(0.16,1,0.3,1)",
      }}
    >
      <Mic className="w-6 h-6 pointer-events-none" />
      <span
        className={`absolute -top-1 -right-1 w-3 h-3 bg-[#B7955B] rounded-full pointer-events-none ${dragging ? "" : "animate-pulse"}`}
      />
    </button>
  );
}
